import { useState, useCallback, useMemo, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  type ColumnDef,
  type ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { AppLayout } from '@/layouts/AppLayout';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ComboboxMultiSelectCount } from '@/components/ui/combobox-multi-select-3';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Currency,
  InquiryPublicDto,
  InquiryStatus,
  OfferStatusValues,
  type ConvertedMoneyDto,
} from '@paladin-monorepo/common';
import { inquiriesApi } from '@/lib/api/inquiries';
import {
  Plus,
  Edit,
  Eye,
  Trash2,
  Send,
  FileText,
  Filter,
  ViewIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuth, type UserProps } from '@/lib/auth';
import {
  canCreateInquiry,
  canEditInquiry,
  canDeleteInquiry,
  canPublishInquiry,
  isConsultantOrSolutionProvider,
  canSubmitOffer,
} from '@/lib/permissions';
import { Loading } from '@/components/Loading';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDate } from '@/utils/formatDate';
import { formatRange } from '@/utils/formatNumber';
import { InquiryStatusBadge, OfferStatusBadge } from '@/components/StatusBadge';
import { ConfirmButton } from '@/components/ConfirmButton';
import { ReadyInquiryDialog } from '@/components/inquiries/ReadyInquiryDialog';
import { getHttpErrorMessage } from '@/lib/httpErrorMessage';
import { toastInquirerCompanyProfileBlocked } from '@/lib/inquirerCompanyProfileError';
import { offersApi } from '@/lib/api/offers';
import { useCurrencyRates } from '@/hooks/useCurrencyRates';
import { cn } from '@/lib/utils';
import { isInquiryOfferDeadlinePassed } from '@/utils/isInquiryOfferDeadlinePassed';
import { InquiryOfferDeadlineDisplay } from '@/components/inquiries/InquiryOfferDeadlineDisplay';

type CreatedAtFilter = { from?: string; to?: string };

function startOfLocalDay(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0);
}

function endOfLocalDay(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d, 23, 59, 59, 999);
}

function matchesCreatedAtFilter(createdAt: string, filter: unknown): boolean {
  if (!filter || typeof filter !== 'object') return true;
  const { from, to } = filter as CreatedAtFilter;
  if (!from && !to) return true;
  const created = new Date(createdAt).getTime();
  if (from && created < startOfLocalDay(from).getTime()) return false;
  if (to && created > endOfLocalDay(to).getTime()) return false;
  return true;
}

function statusFilterSelection(filterValue: unknown): string[] {
  if (filterValue == null || filterValue === '') return [];
  if (Array.isArray(filterValue)) {
    return filterValue.filter((x): x is string => typeof x === 'string');
  }
  return [String(filterValue)];
}

function InquiryColumnFilterHeader({
  title,
  filterOpenLabel,
  filterActive,
  children,
  contentClassName,
}: {
  title: ReactNode;
  filterOpenLabel: string;
  filterActive: boolean;
  children: ReactNode;
  contentClassName?: string;
}) {
  return (
    <div className="flex items-center gap-1">
      <span>{title}</span>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'inline-flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground',
              filterActive &&
                'border border-primary/25 bg-primary/5 text-primary'
            )}
            aria-label={filterOpenLabel}
          >
            <Filter className="size-3.5" aria-hidden />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className={cn('w-auto', contentClassName)}>
          {children}
        </PopoverContent>
      </Popover>
    </div>
  );
}

function matchesStatusFilter(
  brief: InquiryPublicDto,
  user: UserProps | undefined,
  filterValue: unknown
): boolean {
  const selected = statusFilterSelection(filterValue);
  if (selected.length === 0) return true;
  const allowed = new Set(selected);
  if (!user) return allowed.has(brief.status);
  if (isConsultantOrSolutionProvider(user)) {
    if (!brief.userOffer) return false;
    return allowed.has(brief.userOffer.status);
  }
  return allowed.has(brief.status);
}

type InquiryListRowActionsProps = {
  brief: InquiryPublicDto;
  user: UserProps;
  publishingId: string | null;
  deletingId: string | null;
  onPublish: (briefId: string, comment?: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onSubmitOffer: (offerId: string) => Promise<void>;
};

function InquiryListRowActions({
  brief,
  user,
  publishingId,
  deletingId,
  onPublish,
  onDelete,
  onSubmitOffer,
}: InquiryListRowActionsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userOffer = brief.userOffer;
  const isPastDeadline = isInquiryOfferDeadlinePassed(brief.deadline);
  const deadlinePassedTitle = t('offers.form.messages.deadlinePassed');

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <Button
        variant="outline"
        size="sm"
        title={t('projects.view')}
        onClick={() => navigate(`/inquiries/${brief.id}`)}
      >
        <Eye className="h-4 w-4" />
        {/* {t('projects.view')} */}
      </Button>

      {canEditInquiry(user, brief) && (
        <Button
          variant="outline"
          size="sm"
          title={t('projects.edit')}
          onClick={() => navigate(`/inquiries/${brief.id}/edit`)}
        >
          <Edit className="h-4 w-4" />
          {/* {t('projects.edit')} */}
        </Button>
      )}

      {canPublishInquiry(user, brief) &&
        brief.status === InquiryStatus.Draft && (
          <ReadyInquiryDialog
            title={t('inquiries.actions.ready.title')}
            description={t('inquiries.actions.ready.description')}
            onConfirm={(comment) => onPublish(brief.id, comment)}
            variant="outline"
            size="sm"
            buttonType='default'
            disabled={publishingId === brief.id}
          >
            {/* {t('inquiries.actions.ready.actionButton')} */}
          </ReadyInquiryDialog>
        )}

      {canDeleteInquiry(user, brief) && (
        <ConfirmButton
          buttonTitle={t('projects.delete')}
          title={t('projects.messages.confirmDelete')}
          description={t('projects.messages.confirmDeleteDescription')}
          confirmText={t('generic.delete')}
          cancelText={t('generic.cancel')}
          onConfirm={() => onDelete(brief.id)}
          variant="outline"
          disabled={deletingId === brief.id}
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          <Trash2 className="mr-1 h-4 w-4" />
          {/* {t('projects.delete')} */}
        </ConfirmButton>
      )}

      {isConsultantOrSolutionProvider(user) && (
        <>
          {!userOffer && (
            <Button
              variant="outline"
              size="sm"
              disabled={isPastDeadline}
              disabledTooltip={deadlinePassedTitle}
              onClick={() => navigate(`/inquiries/${brief.id}/offers/new`)}
            >
              <FileText className="mr-1 h-4 w-4" />
              {t('offers.actions.createOffer')}
            </Button>
          )}

          {userOffer && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/offers/${userOffer.id}`)}
                title={t('offers.actions.viewOffer')}
              >
                <ViewIcon className="h-4 w-4" />
                {/* {t('offers.actions.viewOffer')} */}
              </Button>

              {brief.status === InquiryStatus.Published &&
                brief.currentVersionNumber &&
                userOffer.inquiryVersionNumber !== undefined &&
                brief.currentVersionNumber > userOffer.inquiryVersionNumber && (
                  <Button
                    variant="default"
                    size="sm"
                    disabled={isPastDeadline}
                    disabledTooltip={deadlinePassedTitle}
                    onClick={() =>
                      navigate(`/inquiries/${brief.id}/offers/new`)
                    }
                  >
                    <FileText className="h-4 w-4" />
                    {t('offers.actions.createNewVersion')}
                  </Button>
                )}

              {canSubmitOffer(user, {
                accountId: user.account?.id ?? '',
                status: userOffer.status,
              }) && (
                <ConfirmButton
                  title={t('offers.submitDialog.title')}
                  description={t('offers.submitDialog.description')}
                  confirmText={t('generic.confirm')}
                  cancelText={t('generic.cancel')}
                  onConfirm={() => onSubmitOffer(userOffer.id)}
                  variant="default"
                >
                  <Send className="mr-1 h-4 w-4" />
                  {t('offers.actions.submitOffer')}
                </ConfirmButton>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

export function InquiriesListPage() {
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth({
    middleware: 'auth',
    redirectTo: '/auth/login',
    tmp: 'InquiriesListPage',
  });

  const queryClient = useQueryClient();
  const { converter } = useCurrencyRates();

  const userCurrency = user?.account?.defaultCurrency as Currency | undefined;

  const convertBudget = useCallback(
    (amount: number, currency: Currency): ConvertedMoneyDto => {
      if (!converter || !userCurrency || currency === userCurrency) {
        return { original: { amount, currency } };
      }
      return converter.convertWithOriginal({ amount, currency }, userCurrency);
    },
    [converter, userCurrency]
  );

  const pageTitle = t('sidebar.inquiries.title');

  const { data: inquiries, isLoading } = useQuery<InquiryPublicDto[]>({
    queryKey: ['inquiries'],
    queryFn: inquiriesApi.getInquiries,
  });

  const deleteMutation = useMutation({
    mutationFn: inquiriesApi.deleteInquiry,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['inquiries'] });
    },
  });

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        setDeletingId(id);
        await deleteMutation.mutateAsync(id);
        toast.success(t('projects.messages.deletedSuccessfully'));
      } catch (error) {
        console.error('Failed to delete project brief:', error);
        toast.error(t('projects.messages.failedToDelete'));
      } finally {
        setDeletingId(null);
      }
    },
    [deleteMutation, t],
  );

  const handlePublish = useCallback(
    async (briefId: string, comment?: string) => {
      try {
        setPublishingId(briefId);
        await inquiriesApi.readyInquiry(briefId, { comment });
        toast.success(t('projects.messages.readySuccessfully'));
        await queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      } catch (error) {
        console.error('Failed to ready project brief:', error);
        if (!toastInquirerCompanyProfileBlocked(error, t, () => navigate('/settings/general'))) {
          toast.error(getHttpErrorMessage(error, t('projects.messages.failedToReady')));
        }
        throw error;
      } finally {
        setPublishingId(null);
      }
    },
    [navigate, queryClient, t]
  );

  const handleSubmitOffer = useCallback(
    async (offerId: string) => {
      if (!offerId) return;
      try {
        await offersApi.submit(offerId);
        toast.success(t('offers.form.messages.submittedSuccessfully'));
        await queryClient.invalidateQueries({ queryKey: ['offer', offerId] });
        await queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      } catch (error) {
        console.error('Failed to submit offer:', error);
        toast.error(t('offers.form.messages.failedToSubmit'));
      }
    },
    [queryClient, t]
  );

  const columns = useMemo<ColumnDef<InquiryPublicDto>[]>(
    () => {
      const statusOptionsForFilter = isConsultantOrSolutionProvider(user)
        ? OfferStatusValues.map((s) => ({
            value: s,
            label: t(`offers.status.${s}`),
          }))
        : (Object.values(InquiryStatus) as InquiryStatus[]).map((s) => ({
            value: s,
            label: t(`inquiries.status.${s}`),
          }));

      return [
      {
        id: 'inquiry',
        accessorKey: 'title',
        filterFn: (row, _columnId, filterValue) => {
          const q = String(filterValue ?? '')
            .trim()
            .toLowerCase();
          if (!q) return true;
          return row.original.title.toLowerCase().includes(q);
        },
        header: ({ column }) => {
          const q = ((column.getFilterValue() as string) ?? '').trim();
          return (
            <InquiryColumnFilterHeader
              title={t('inquiries.table.columns.title')}
              filterOpenLabel={t('inquiries.table.filters.openTitleFilter')}
              filterActive={Boolean(q)}
              contentClassName="min-w-[16rem]"
            >
              <Input
                placeholder={t('inquiries.table.filters.titlePlaceholder')}
                value={(column.getFilterValue() as string) ?? ''}
                onChange={(e) =>
                  column.setFilterValue(e.target.value || undefined)
                }
                className="h-8 max-w-full"
                aria-label={t('inquiries.table.filters.titlePlaceholder')}
              />
            </InquiryColumnFilterHeader>
          );
        },
        cell: ({ row }) => {
          const brief = row.original;
          return (
            <Link
              to={`/inquiries/${brief.id}`}
              className="block text-body font-medium text-brand-secondary hover:underline whitespace-normal break-words"
            >
              {brief.title}
            </Link>
          );
        },
      },
      {
        id: 'status',
        filterFn: (row, _columnId, filterValue) =>
          matchesStatusFilter(row.original, user, filterValue),
        header: ({ column }) => {
          const selected = statusFilterSelection(column.getFilterValue());
          return (
            <InquiryColumnFilterHeader
              title={t('inquiries.table.columns.status')}
              filterOpenLabel={t('inquiries.table.filters.openStatusFilter')}
              filterActive={selected.length > 0}
              contentClassName="min-w-[12rem]"
            >
              <ComboboxMultiSelectCount
                options={statusOptionsForFilter}
                value={selected}
                onChange={(next) =>
                  column.setFilterValue(next.length ? next : undefined)
                }
                placeholder={t('inquiries.table.filters.statusAll')}
                formatSelectionSummary={(count) =>
                  t('inquiries.table.filters.statusSelectedCount', { count })
                }
                searchPlaceholder={t('profile.multiSelect.searchPlaceholder')}
                emptyText={t('profile.multiSelect.noResults')}
                aria-label={t('inquiries.table.columns.status')}
                className="min-w-[9rem]"
              />
            </InquiryColumnFilterHeader>
          );
        },
        cell: ({ row }) => {
          const brief = row.original;
          return (
            <div className="flex flex-col items-start gap-1">
              {!isConsultantOrSolutionProvider(user) && (
                <InquiryStatusBadge status={brief.status} />
              )}
              {isConsultantOrSolutionProvider(user) && brief.userOffer && (
                <OfferStatusBadge
                  status={brief.userOffer.status}
                  rejected={
                    'rejectionReason' in brief.userOffer &&
                    (brief.userOffer as { rejectionReason?: unknown })
                      .rejectionReason !== null
                  }
                />
              )}
            </div>
          );
        },
      },
      {
        id: 'created',
        accessorKey: 'createdAt',
        filterFn: (row, _columnId, filterValue) =>
          matchesCreatedAtFilter(row.original.createdAt, filterValue),
        header: ({ column }) => {
          const val = (column.getFilterValue() as CreatedAtFilter | undefined) ?? {};
          const setRange = (next: CreatedAtFilter) => {
            if (!next.from && !next.to) column.setFilterValue(undefined);
            else column.setFilterValue(next);
          };
          const rangeActive = Boolean(val.from || val.to);
          return (
            <InquiryColumnFilterHeader
              title={t('inquiries.table.columns.createdAt')}
              filterOpenLabel={t('inquiries.table.filters.openDateFilter')}
              filterActive={rangeActive}
              contentClassName="min-w-[16rem]"
            >
              <div className="flex min-w-0 flex-col gap-2">
                <Input
                  type="date"
                  className="h-8 min-w-0 flex-1"
                  value={val.from ?? ''}
                  onChange={(e) =>
                    setRange({ ...val, from: e.target.value || undefined })
                  }
                  aria-label={t('inquiries.table.filters.dateFrom')}
                />
                <Input
                  type="date"
                  className="h-8 min-w-0 flex-1"
                  value={val.to ?? ''}
                  onChange={(e) =>
                    setRange({ ...val, to: e.target.value || undefined })
                  }
                  aria-label={t('inquiries.table.filters.dateTo')}
                />
              </div>
            </InquiryColumnFilterHeader>
          );
        },
        cell: ({ row }) => (
          <span className="tabular-nums text-text-secondary">
            {formatDate(row.original.createdAt)}
          </span>
        ),
      },
      {
        id: 'offerDeadline',
        accessorKey: 'deadline',
        header: () => t('inquiries.table.columns.offerDeadline'),
        cell: ({ row }) => (
          <InquiryOfferDeadlineDisplay deadline={row.original.deadline} />
        ),
      },
      {
        id: 'budget',
        header: () => t('inquiries.table.columns.budget'),
        cell: ({ row }) => {
          const brief = row.original;
          if (brief.budgetMin == null) {
            return (
              <span className="text-text-disabled">
                {t('projects.form.review.notSpecified')}
              </span>
            );
          }
          const minMoney = convertBudget(brief.budgetMin, brief.currency);
          const maxMoney =
            brief.budgetMax != null
              ? convertBudget(brief.budgetMax, brief.currency)
              : null;
          const amount = (m: ConvertedMoneyDto) => m.converted?.amount ?? m.original.amount;
          const code = (m: ConvertedMoneyDto) => m.converted?.currency ?? m.original.currency;
          const currency = code(maxMoney ?? minMoney);

          return (
            <span className="tabular-nums text-text-secondary">
              {formatRange(amount(minMoney), maxMoney != null ? amount(maxMoney) : null, {
                currency,
              })}
            </span>
          );
        },
      },
      {
        id: 'actions',
        header: () => t('generic.actions'),
        cell: ({ row }) => {
          const brief = row.original;
          if (!user) return null;
          return (
            <InquiryListRowActions
              brief={brief}
              user={user}
              publishingId={publishingId}
              deletingId={deletingId}
              onPublish={handlePublish}
              onDelete={handleDelete}
              onSubmitOffer={handleSubmitOffer}
            />
          );
        },
      },
    ];
    },
    [
      t,
      user,
      convertBudget,
      publishingId,
      deletingId,
      handlePublish,
      handleDelete,
      handleSubmitOffer,
    ]
  );

  const table = useReactTable({
    data: inquiries ?? [],
    columns,
    state: { columnFilters },
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const filteredRows = table.getRowModel().rows;

  if (isLoading) {
    return (
      <AppLayout
        breadcrumbs={[{ href: '/inquiries', children: t('inquiries.title') }]}
      >
        <div className="container mx-auto flex h-64 items-center justify-center p-6">
          <div className="text-center">
            <Loading text={t('generic.loading')} />
          </div>
        </div>
      </AppLayout>
    );
  }

  const canCreate = user && canCreateInquiry(user);

  return (
    <AppLayout
      breadcrumbs={[{ href: '/inquiries', children: t('inquiries.title') }]}
    >
      <div className="container mx-auto space-y-6 p-6">
        <PageHeader
          title={pageTitle}
          description={
            canCreate
              ? t('inquiries.description.inquirer')
              : t('inquiries.description.consultant')
          }
          actions={
            canCreate ? (
              <Button asChild>
                <Link to="/inquiries/new">
                  <Plus className="mr-2 h-4 w-4" />
                  {t('inquiries.actions.createNew')}
                </Link>
              </Button>
            ) : undefined
          }
        />

        {!inquiries || inquiries?.length === 0 ? (
          <Card className="rounded-sm border-border-subtle shadow-none">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <div className="text-center">
                <h3 className="text-title text-foreground mb-2">
                  {t('inquiries.empty.title')}
                </h3>
                {canCreate && (
                  <>
                    <p className="text-body text-muted-foreground mb-4">
                      {t('projects.empty.description')}
                    </p>
                    <Button asChild>
                      <Link to="/inquiries/new">
                        <Plus className="mr-2 h-4 w-4" />
                        {t('projects.createFirst')}
                      </Link>
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="rounded-sm border-border-subtle py-0 shadow-none">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow
                      key={headerGroup.id}
                      className="hover:bg-transparent"
                    >
                      {headerGroup.headers.map((header) => (
                        <TableHead
                          key={header.id}
                          className={cn(
                            'px-5',
                            header.column.id === 'inquiry' && 'min-w-[16rem] w-[28%]',
                            header.column.id === 'actions' &&
                              'xl:w-[28rem] text-right'
                          )}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {filteredRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={table.getAllColumns().length}
                        className="px-5 py-10 text-center text-muted-foreground"
                      >
                        {t('inquiries.table.filters.noResults')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRows.map((row) => (
                      <TableRow key={row.id}>
                        {row.getVisibleCells().map((cell) => (
                          <TableCell
                            key={cell.id}
                            className={cn(
                              'px-5',
                              cell.column.id === 'inquiry' &&
                                'min-w-[16rem] whitespace-normal break-words normal-case',
                              cell.column.id === 'actions' &&
                                'whitespace-normal text-right',
                            )}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}
